import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { H4 } from '@blueprintjs/core';
import { Grid, Typography as BaseTypography } from 'components/base';
import useModalContext from 'components/context/modal';
import ProductConfiguration from 'ecommerce/components/productConfiguration';
import dataApi from 'data/api/data';
import { formatPrice } from 'utils/format';

import EditorLayout from '../new/components/layout';
import { Button, Panel, Typography } from '../components/base';
import useEditorContext from '../context/editorContext';
import treesApi from '../api/trees';
import FamilyTreeSelect from '../components/ancestry/familyTreeSelect';
import { useNewFamilyTreeModal } from './components/newFamilyModal';
import withHocTreeEditor from './hoc';
import CoupleSelectTabs from '../contentWizard/people/couples/coupleSelectTabs';
import AncestryWebSocketSynchronizer from '../new/components/ancestryWebSocketSynchronizer';
import generateMyCanvasData from '../generator/generateMyCanvasData';
import NumberOfGenerationsSelect from '../components/ancestry/numberOfGenerationsSelect';
import { getDefaultNumberOfGenerations, getNumbersOfGenerations } from 'editor-utils/template';
import ImportGedcomFileInput from 'editor/tree/components/importGedcomFileInput';

const TreeEditorSource = props => {
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const newFamilyTreeModal = useNewFamilyTreeModal(editorContext.accessToken);

  const [treeConfiguration, setTreeConfiguration] = useState({});
  /**
   * Configuration { product, primaryProductData, secondaryProductData, price, bundle }
   */
  const [productConfiguration, setProductConfiguration] = useState({});
  const product = useMemo(() => {
    return {
      ...props.product,
      reducer: props.product.config?.editorReducer ?? props.product.reducer,
    };
  }, [props.product]);

  const isPreviewEnabled = useMemo(() => {
    if (!treeConfiguration.personGid) {
      return false;
    }
    if (!productConfiguration.product) {
      return false;
    }

    return true;
  }, [treeConfiguration, productConfiguration]);

  const [skuProduct, setSkuProduct] = useState(product.options[0]);

  const editorConfig = useMemo(() => {
    return editorContext.getConfiguration(skuProduct.id, skuProduct.version);
  }, [skuProduct]);

  const [selectedNumberOfGenerations, setSelectedNumberOfGenerations] = useState(
    getDefaultNumberOfGenerations(null, editorConfig.numbersOfGenerations),
  );

  const numbersOfGenerations = useMemo(
    () => getNumbersOfGenerations(null, editorConfig.numbersOfGenerations),
    [editorConfig],
  );

  const [selectedTree, setSelectedTree] = useState();
  const [trees, setTrees] = useState([]);

  const reloadTrees = async () => {
    const { data, error } = await treesApi(editorContext.accessToken).getTrees();

    if (data) {
      setTrees(data);
    }
  };

  useEffect(() => {
    reloadTrees();
  }, []);

  const handleOnTreeChange = async tree => {
    const { data: persons, error } = await treesApi(editorContext.accessToken).getTreePersons(
      tree.id,
    );
    if (error) {
      console.log(error);
      setSelectedTree();
      return undefined;
    }

    const value = { ...tree, persons };
    setSelectedTree(value);
    return value;
  };

  const handleOnNewTreeClick = async () => {
    newFamilyTreeModal.open(async tree => {
      reloadTrees();

      const value = await handleOnTreeChange(tree);
      if (value) {
        props.startTreeEditor(value, () => {
          handleOnTreeChange(value);
        });
      }
    });
  };

  const handleDeleteTreeClick = async e => {
    e.preventDefault();

    const { data, error } = await treesApi(editorContext.accessToken).deleteTree(selectedTree.id);

    reloadTrees();
    setSelectedTree();
  };

  const handleOnPreviewClick = async e => {
    e.preventDefault();

    const { treeId, treeName, personGid, partnerGid } = treeConfiguration;

    try {
      gtag('event', 'tree_generate_print', {
        event_category: 'trees',
        event_label: treeId,
      });
    } catch (e) { }

    const numberOfGenerations =
      selectedNumberOfGenerations ?? parseInt(productConfiguration.config.numberOfGenerations, 10);

    modalContext.show(
      <AncestryWebSocketSynchronizer
        accessToken={editorContext.accessToken}
        productId={skuProduct.id}
        treeName={treeName}
        persons={[personGid, partnerGid]}
        numberOfGenerations={numberOfGenerations}
        onSuccess={async ({ onProgress }) => {
          const { data: template, error: templateError } = await dataApi.getTemplate(
            skuProduct.id,
            skuProduct.version,
          );

          if (templateError) {
            props.onSuccess(() => ({ error: templateError }));
            return;
          }

          const { data, error } = await generateMyCanvasData(
            editorContext.accessToken,
            template,
            {
              treeId,
              treeName,
              personGid,
              partnerGid,
              numberOfGenerations,
            },
            [],
            ({ pending, total }) => {
              onProgress(((total - pending) / total) * 100);
            },
          );

          if (error) {
            editorContext.trackEvent('mycanvas_generator_error');
            try {
              gtag('event', 'tree_generate_print_error', {
                event_category: 'trees',
                event_label: treeId,
              });
            } catch (e) { }
          } else {
            try {
              gtag('event', 'tree_preview_print', {
                event_category: 'trees',
                event_label: treeId,
              });
            } catch (e) { }
          }

          props.onSuccess(() => ({ data, error }), { skuProduct, template });
        }}
      />,
      {
        title: 'Building your project',
        closeable: false,
      },
    );
  };

  const handleOnChangeConfiguration = data => {
    if (data.product) {
      setSkuProduct(data.product);
    }
    setProductConfiguration(data);
  };

  return (
    <EditorLayout
      product={props.product}
      showPrice={false}
      actionButton={
        <Button disabled={!isPreviewEnabled} variant="primary" onClick={handleOnPreviewClick}>
          Preview
        </Button>
      }
      primaryChildren={props.children}
    >
      <div className="mw-1280 mx-auto">
        <Panel borderColor="green">
          <H4>Select a family tree</H4>
          <Grid.Row>
            <Grid.Col xs={12} md={6} className="d-flex">
              {trees.length > 0 && (
                <FamilyTreeSelect
                  trees={trees}
                  labelKey="caption"
                  selectedTree={selectedTree}
                  onChange={handleOnTreeChange}
                  className="mw-300 mr-1"
                  fill
                />
              )}
              {selectedTree && !selectedTree.imported && (
                <Button
                  variant="primary"
                  onClick={() =>
                    props.startTreeEditor(selectedTree, () => {
                      reloadTrees();
                      setSelectedTree()
                    })
                  }
                >
                  Edit
                </Button>
              )}

            </Grid.Col>

            <Grid.Col xs={12} md={6} className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleOnNewTreeClick} className="mr-1">
                Create a New Family Tree
              </Button>

              <ImportGedcomFileInput
                accessToken={editorContext.accessToken}
                onSuccess={reloadTrees}
                trigger={({ onClick, isProcessing }) => (
                  <Button variant="primary" onClick={onClick} isLoading={isProcessing}>
                    Import GEDCOM File
                  </Button>
                )}
              />


            </Grid.Col>
          </Grid.Row>

          {selectedTree && (
            <React.Fragment key={selectedTree.id}>
              <H4 className="mt-3">Select a starting person</H4>
              <CoupleSelectTabs
                key={selectedTree.id}
                people={selectedTree.persons.map(i => ({
                  id: i.id,
                  gid: i.gid,
                  birth: i.birthDate?.y,
                  death: i.deathDate?.y,
                  name: [i.first, i.last].join(' '),
                  avatarUrl:
                    i.gender === 'female'
                      ? '/static/img/female-avatar.png'
                      : '/static/img/male-avatar.png',
                  pids: i.pids,
                }))}
                onSelect={event => {
                  setTreeConfiguration({
                    treeId: selectedTree.id,
                    treeName: selectedTree.caption,
                    personGid: event.person?.gid,
                    partnerGid: event.partner?.gid,
                  });
                }}
              />
            </React.Fragment>
          )}
        </Panel>

        {treeConfiguration.personGid && (
          <Panel borderColor="orange" className="mt-3 pb-0">
            <H4>Configure your creation</H4>
            <Grid.Row>
              <Grid.Col xs={12}>
                <ProductConfiguration
                  product={product}
                  skuProduct={skuProduct}
                  onChange={handleOnChangeConfiguration}
                  cardClassName="card-mycanvas"
                  priceLabelClassName="text-body"
                  hidden={product.config.hideProductConfiguration}
                  defaultPrintOnly={product.config.defaultPrintOnly ?? true}
                  autoItemsWidth
                />
                <BaseTypography.H6>PRICE</BaseTypography.H6>
                <BaseTypography.H3 className="mb-3">
                  {formatPrice(productConfiguration.price, product.currency, true)}
                </BaseTypography.H3>
              </Grid.Col>

              {numbersOfGenerations.length > 1 && (
                <Grid.Col xs={12} className="my-1">
                  <H4>Select the number of generations</H4>
                  <Grid.Row>
                    <Grid.Col xs={12} sm={6} className="mw-300">
                      <NumberOfGenerationsSelect
                        fill
                        values={numbersOfGenerations}
                        selectedValue={selectedNumberOfGenerations}
                        onChange={setSelectedNumberOfGenerations}
                      />
                    </Grid.Col>
                    <Grid.Col xs={12} sm={6} className="d-flex align-items-center">
                      <Typography.Paragraph mb={0}>
                        Select the number of generations you want included in your creation.
                      </Typography.Paragraph>
                    </Grid.Col>
                  </Grid.Row>
                </Grid.Col>
              )}

              <Grid.Col xs={12} className="d-flex align-items-end">
                <div className="text-right w-100 mb-3">
                  <Button
                    large
                    variant="primary"
                    disabled={!isPreviewEnabled}
                    onClick={handleOnPreviewClick}
                  >
                    Preview your creation
                  </Button>
                </div>
              </Grid.Col>
            </Grid.Row>
          </Panel>
        )}
      </div>
    </EditorLayout>
  );
};

TreeEditorSource.propTypes = {
  product: PropTypes.object,
  query: PropTypes.object,
  onSuccess: PropTypes.func,
  children: PropTypes.node,
};

export default withHocTreeEditor(TreeEditorSource);
